'use client';

import { type ReactElement, useReducer } from 'react';
import { CalendarBase, CalendarUpcoming, Description, Filter, Label, Priority, Project, Subtask } from '@doist/icons/brand';
import { Quote } from '@doist/marketist';
import { FeatureRadioGroup } from 'components/feature-radio-tile/feature-radio-group';
import { RadioTileScene, SceneWrapper } from 'components/feature-radio-tile/scene-images';
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context';
import { FiltersScene } from 'components/product-ui/scenes/filters';
import { LabelsScene } from 'components/product-ui/scenes/labels';
import { PrioritiesScene } from 'components/product-ui/scenes/priorities';
import { ProjectsScene } from 'components/product-ui/scenes/projects';
import { SectionsSubtasksScene } from 'components/product-ui/scenes/sections-subtasks';
import { TaskDescriptionsScene } from 'components/product-ui/scenes/task-descriptions';
import { TodayViewScene } from 'components/product-ui/scenes/today-view';
import { UpcomingViewScene } from 'components/product-ui/scenes/upcoming-view';
import { ScrollHeader } from 'components/scrolling-section/scroll-header/scroll-header';
import { ScrollSection } from 'components/scrolling-section/scroll-section/scroll-section';
import { useTranslation } from 'next-i18next';
import { useLocalisedContent } from 'services/hooks/use-localised-data';
import { quoteOrganisePrioritise, quotePedro } from './quote-map';
const OrganiseTile = ['projects', 'priorities', 'labels', 'descriptions', 'sectionsSubTasks'] as const;
const focusTiles = ['upcoming', 'today', 'filters'] as const;
type RadioTile = (typeof OrganiseTile)[number];
type FocusTile = (typeof focusTiles)[number];
type AllTile = RadioTile | FocusTile;
type RadioGroupAction = {
  type: 'updateOrganise';
  value?: RadioTile;
} | {
  type: 'updateFocus';
  value?: FocusTile;
};
type GroupState = {
  activeRadioTile: RadioTile;
  activeFocusTile: FocusTile;
  currentTile: AllTile;
};
const initialState: GroupState = {
  activeRadioTile: 'projects',
  activeFocusTile: 'upcoming',
  currentTile: 'projects'
};
function radioGroupReducer(state: GroupState, action: RadioGroupAction): GroupState {
  switch (action.type) {
    case 'updateOrganise':
      {
        return {
          ...state,
          activeRadioTile: action.value || state.activeRadioTile,
          currentTile: action.value || state.activeRadioTile
        };
      }
    case 'updateFocus':
      {
        return {
          ...state,
          activeFocusTile: action.value || state.activeFocusTile,
          currentTile: action.value || state.activeFocusTile
        };
      }
    default:
      return state;
  }
}
export function OrganisePrioritise() {
  const {
    t
  } = useTranslation('features-teams');
  const avatarSrc = useLocalisedContent(quoteOrganisePrioritise);
  const pedroAvatarSrc = useLocalisedContent(quotePedro);
  const [state, dispatch] = useReducer(radioGroupReducer, initialState);
  function organiseChangeHandler(value: RadioTile) {
    dispatch({
      type: 'updateOrganise',
      value
    });
  }
  function focusChangeHandler(value: FocusTile) {
    dispatch({
      type: 'updateFocus',
      value
    });
  }
  const sceneImages: { [key in AllTile]: ReactElement } = {
    projects: <ProjectsScene />,
    priorities: <PrioritiesScene />,
    labels: <LabelsScene />,
    descriptions: <TaskDescriptionsScene />,
    sectionsSubTasks: <SectionsSubtasksScene />,
    filters: <FiltersScene />,
    today: <TodayViewScene />,
    upcoming: <UpcomingViewScene />
  };
  return <ProductUIThemeProvider theme="green" data-sentry-element="ProductUIThemeProvider" data-sentry-component="OrganisePrioritise" data-sentry-source-file="organise-prioritise.tsx">
            <ScrollSection data-sentry-element="ScrollSection" data-sentry-source-file="organise-prioritise.tsx">
                <ScrollSection.StickySection data-sentry-element="unknown" data-sentry-source-file="organise-prioritise.tsx">
                    <SceneWrapper scene={sceneImages[state.currentTile]} data-sentry-element="SceneWrapper" data-sentry-source-file="organise-prioritise.tsx" />
                </ScrollSection.StickySection>
                <ScrollSection.StaticSection data-sentry-element="unknown" data-sentry-source-file="organise-prioritise.tsx">
                    <ScrollHeader title={t('organizePrioritize')} spotIllustration={{
          src: '/static/features-teams/features_spot2.png',
          offset: {
            x: -103,
            y: -39
          },
          mobileOffset: {
            x: -44,
            y: -48
          }
        }} data-sentry-element="ScrollHeader" data-sentry-source-file="organise-prioritise.tsx">
                        {t('allTheTools')}
                    </ScrollHeader>

                    <FeatureRadioGroup<RadioTile> onChange={organiseChangeHandler} value={state.activeRadioTile} namespace="features-teams" className="md-mbe-192 mbe-96" radioTiles={[{
          titleKey: 'projects',
          bodyKey: 'projectsBody',
          icon: <Project />,
          scene: <RadioTileScene scene={sceneImages.projects} />
        }, {
          titleKey: 'priorities',
          bodyKey: 'prioritiesBody',
          icon: <Priority />,
          scene: <RadioTileScene scene={sceneImages.priorities} />
        }, {
          titleKey: 'labels',
          bodyKey: 'labelsBody',
          icon: <Label />,
          scene: <RadioTileScene scene={sceneImages.labels} />
        }, {
          titleKey: 'descriptions',
          bodyKey: 'descriptionsBody',
          icon: <Description />,
          scene: <RadioTileScene scene={sceneImages.descriptions} />
        }, {
          titleKey: 'sectionsSubTasks',
          bodyKey: 'sectionsSubTasksBody',
          icon: <Subtask />,
          scene: <RadioTileScene scene={sceneImages.sectionsSubTasks} />
        }]} data-sentry-element="FeatureRadioGroup" data-sentry-source-file="organise-prioritise.tsx" />

                    <Quote quoteText={t('quoteMaria.quote')} author={t('quoteMaria.name')} role={t('quoteMaria.role')} avatarSrc={`/static/features-teams/avatars/${avatarSrc}`} className="mbe-96 md-mbe-192" data-sentry-element="Quote" data-sentry-source-file="organise-prioritise.tsx" />

                    <ScrollSection.Trigger onEnter={() => {
          dispatch({
            type: 'updateFocus'
          });
        }} onEnterBack={() => {
          dispatch({
            type: 'updateOrganise'
          });
        }} data-sentry-element="unknown" data-sentry-source-file="organise-prioritise.tsx">
                        <ScrollHeader title={t('focus')} spotIllustration={{
            src: '/static/features-teams/features_spot3.png',
            offset: {
              x: -106,
              y: -63
            },
            mobileOffset: {
              x: -27,
              y: -60
            }
          }} data-sentry-element="ScrollHeader" data-sentry-source-file="organise-prioritise.tsx">
                            {t('infinitelyFlexible')}
                        </ScrollHeader>
                    </ScrollSection.Trigger>

                    <FeatureRadioGroup<FocusTile> onChange={focusChangeHandler} value={state.activeFocusTile} namespace="features-teams" className="md-mbe-192 mbe-96" radioTiles={[{
          titleKey: 'upcoming',
          bodyKey: 'upcomingBody',
          icon: <CalendarUpcoming />,
          scene: <RadioTileScene scene={sceneImages.upcoming} />
        }, {
          titleKey: 'today',
          bodyKey: 'todayBody',
          icon: <CalendarBase />,
          scene: <RadioTileScene scene={sceneImages.today} />
        }, {
          titleKey: 'filters',
          bodyKey: 'filtersBody',
          icon: <Filter />,
          scene: <RadioTileScene scene={sceneImages.filters} />
        }]} data-sentry-element="FeatureRadioGroup" data-sentry-source-file="organise-prioritise.tsx" />

                    <ScrollSection.StickyAnchor data-sentry-element="unknown" data-sentry-source-file="organise-prioritise.tsx">
                        <Quote quoteText={t('quotePedro.quote')} author={t('quotePedro.name')} role={t('quotePedro.role')} avatarSrc={`/static/features-teams/avatars/${pedroAvatarSrc}`} data-sentry-element="Quote" data-sentry-source-file="organise-prioritise.tsx" />
                    </ScrollSection.StickyAnchor>
                </ScrollSection.StaticSection>
            </ScrollSection>
        </ProductUIThemeProvider>;
}